import { Link } from "react-router";
import { Icon, type IconName } from "../components/icons";
import { PlatformStatus } from "../components/Status";
import { ErrorNote, PageHead, Section } from "../components/kit";
import type { Overview } from "../lib/api";
import { useResource } from "../lib/hooks";
import { AuditList } from "./Audit";

const ACTIONS: { to: string; label: string; note: string; icon: IconName }[] = [
  { to: "/accounts?new=1", label: "New account", note: "Someone who signs in", icon: "accounts" },
  { to: "/portals/new", label: "Register a portal", note: "A site that signs people in", icon: "portals" },
  { to: "/settings", label: "SMS and sign-on", note: "Configuration", icon: "key" },
];

export function OverviewPage() {
  const { data, error } = useResource<Overview>("/overview");
  const share = data && data.accounts.total ? Math.round((data.accounts.two_step / data.accounts.total) * 100) : 0;
  const stats: { to: string; label: string; icon: IconName; value: number; note: string; meter?: number }[] = data
    ? [
        { to: "/accounts", label: "Accounts", icon: "accounts", value: data.accounts.total, note: `${share}% use two-step sign-in`, meter: share },
        { to: "/accounts?filter=admins", label: "Administrators", icon: "shield", value: data.accounts.admins, note: "Enabled, can open this panel" },
        { to: "/accounts?filter=disabled", label: "Disabled", icon: "signout", value: data.accounts.disabled, note: "Cannot sign in anywhere" },
        { to: "/portals", label: "Portals", icon: "portals", value: data.clients.active, note: `${data.clients.customer} for customers` },
      ]
    : [];
  return (
    <>
      <PageHead title="Overview" lede="Who can sign in, the portals that sign them in, and what administrators changed most recently." />
      <ErrorNote error={error} />
      <ul role="list" className="stats" aria-busy={!data}>
        {data
          ? stats.map((s) => (
              <li key={s.label}>
                <Link to={s.to} className="stat">
                  <span className="stat-top">
                    <span className="stat-label">{s.label}</span>
                    <span className="stat-icon">
                      <Icon name={s.icon} />
                    </span>
                  </span>
                  <span className="stat-value">{s.value}</span>
                  {s.meter !== undefined && (
                    <span className="meter" aria-hidden="true">
                      <span style={{ width: `${s.meter}%` }} />
                    </span>
                  )}
                  <span className="stat-note">{s.note}</span>
                </Link>
              </li>
            ))
          : [0, 1, 2, 3].map((i) => <li key={i} className="stat stat-skeleton" />)}
      </ul>
      <div className="split">
        <Section
          title="Recent changes"
          aside={
            <Link to="/audit" className="panel-link">
              Whole record
            </Link>
          }
        >
          {data && <AuditList entries={data.recent} />}
        </Section>
        <div className="stack">
          <PlatformStatus />
          <Section title="Quick actions">
            <ul role="list" className="quick">
              {ACTIONS.map((a) => (
                <li key={a.to}>
                  <Link to={a.to} className="quick-link">
                    <span className="quick-icon">
                      <Icon name={a.icon} />
                    </span>
                    <span className="quick-text">
                      <span className="quick-label">{a.label}</span>
                      <span className="quick-note">{a.note}</span>
                    </span>
                    <Icon name="chevron" size={16} />
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>
    </>
  );
}
